import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Button from "components/common/Button";
import { useEffect, useState, useMemo } from "react";

/**
 * Ordered engine list from analyticsConfig.engineConfig for "Change Pricing Model".
 * Sort: priority desc (0 if missing), then name asc, then key asc.
 * Returns [{ id, label }] with label = name || id.
 */
const buildOrderedEngineList = (engineConfig) => {
  if (!engineConfig || typeof engineConfig !== "object") {
    return [];
  }
  const entries = Object.entries(engineConfig).map(([key, config]) => ({
    id: key,
    priority: config?.priority ?? 0,
    name: config?.name ?? null,
    label: config?.name ?? key,
  }));
  entries.sort((a, b) => {
    if (b.priority !== a.priority) return a.priority - b.priority;
    const nameA = a.name ?? a.id;
    const nameB = b.name ?? b.id;
    const nameCmp = String(nameB).localeCompare(String(nameA));
    if (nameCmp !== 0) return nameCmp;
    return String(b.id).localeCompare(String(a.id));
  });
  return entries.map(({ id, label }) => ({ id, label }));
};

/**
 * Dialog to change the pricing model (engine). Takes the currently selected
 * model value and a function to set the model on confirm.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {() => void} onClose - Called when the dialog is closed (Cancel or backdrop)
 * @param {string} value - Currently selected engine id
 * @param {(engineId: string) => void} onModelChange - Called with the new engine id when user clicks Apply
 * @param {Record<string, { priority?: number; name?: string }>} [engineConfig] - Analytics engine config (static_data)
 */
const ChangePricingModelDialog = ({
  open,
  onClose,
  value,
  onModelChange,
  engineConfig,
}) => {
  const [selectedEngineInDialog, setSelectedEngineInDialog] = useState(null);
  const engineList = useMemo(() => buildOrderedEngineList(engineConfig), [
    engineConfig,
  ]);
  const displayEngineOptions =
    value && !engineList.some((e) => e.id === value)
      ? [{ id: value, label: "Unknown Engine" }, ...engineList]
      : engineList;

  useEffect(() => {
    if (open) {
      setSelectedEngineInDialog(value ?? null);
    }
  }, [open, value]);

  const handleConfirm = () => {
    const newEngine = selectedEngineInDialog ?? value;
    if (!newEngine || newEngine === value) {
      onClose();
      return;
    }
    onModelChange(newEngine);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>{"Change Pricing Model"}</DialogTitle>
      <DialogContent>
        <FormControl
          variant={"filled"}
          fullWidth
          size={"medium"}
          sx={{ mt: 1 }}
        >
          <InputLabel>{"Pricing engine"}</InputLabel>
          <Select
            value={selectedEngineInDialog ?? value ?? ""}
            onChange={(e) => setSelectedEngineInDialog(e.target.value)}
            label={"Pricing engine"}
          >
            {displayEngineOptions.map(({ id, label }) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={"secondary"}>
          {"Cancel"}
        </Button>
        <Button onClick={handleConfirm}>{"Apply"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePricingModelDialog;
